// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TitleTypography, { TitleTypographyStyles } from "@awc/App/Components/Common/TitleTypography";
import loadFeaturedCategories from "@awc/App/Handlers/Categories/LoadFeaturedCategories";
import { CustomFieldDefinition } from "@awc/CustomFieldDefinition";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import { xParserOptions } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface CategoryModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        name: string;
        image: string;
    };
}

const enum fields {
    title = "title",
    description = "description",
    categoriesList = "categoriesList",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.categoriesList]: CategoryModel[];
    };
}

const mapDispatchToProps = {
    loadFeaturedCategories,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface FeaturedCategoriesStyles {
    container?: InjectableCss;
    titleTypographyStyles?: TitleTypographyStyles;
    description?: TypographyPresentationProps;
    categoriesContainer?: InjectableCss;
    categoryContainer?: InjectableCss;
    categoryImage?: LazyImageProps;
    categoryLink?: LinkPresentationProps;
    clickableImage?: ClickablePresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {};

const styles = featuredCategoriesStyles;

interface FeaturedCategoryItemProps {
    category: CategoryModel;
    path: string;
    styles: FeaturedCategoriesStyles;
}

const FeaturedCategoryItem: React.FC<FeaturedCategoryItemProps> = ({ category, path, styles }) => {
    const destination = category.fields.destination;
    
    const url = destination.type === "Category" && destination.value ? path : useGetLink(destination).url;

    return (
        <StyledWrapper {...styles.categoryContainer} data-test-selector="FeaturedCategories-category-item" as="article">
            <Clickable href={url} {...styles.clickableImage}>
                <LazyImage src={category.fields?.image} altText={category.fields?.name} {...styles.categoryImage} />
            </Clickable>
            <Link
                aria-label={category.fields?.name}
                href={url}
                target={category.fields?.openInNewWindow ? "_blank" : ""}
                {...styles.categoryLink}
            >
                {category.fields?.name}
            </Link>
        </StyledWrapper>
    );
};
const FeaturedCategories = ({ fields: { title, description, categoriesList }, loadFeaturedCategories }: Props) => {
    const toaster = useContext(ToasterContext);
    const [loadedCategories, setLoadedCategories] = React.useState<Category[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);

    const categoryIds = React.useMemo(() => {
        const ids: string[] = [];
        categoriesList.forEach(item => {
            const linkField = item.fields.destination;
            if (linkField.type === "Category" && linkField.value) {
                ids.push(linkField.value);
            }
        });
        return ids;
    }, [categoriesList]);

    React.useEffect(() => {
        if (!categoryIds.length) {
            return;
        }

        setIsLoading(true);
        loadFeaturedCategories({
            categories: categoryIds,
            onSuccess: result => {
                if (result.successful && result.result) {
                    setLoadedCategories(result.result);
                }
            },
            onError: () => {
                if (toaster) {
                    toaster.addToast({
                        body: translate("Error loading Categories."),
                        messageType: "danger",
                    });
                }
            },
            onComplete: () => {
                setIsLoading(false);
            },
        });
    }, [categoryIds, loadFeaturedCategories, toaster]);

    const categoryMap = React.useMemo(() => {
        const map: Record<string, Category> = {};
        loadedCategories.forEach(cat => {
            map[cat.id] = cat;
        });
        return map;
    }, [loadedCategories]);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper as="section" {...styles.container} data-test-selector="FeaturedCategories">
            <TitleTypography extendedStyles={styles.titleTypographyStyles}>{title}</TitleTypography>
            {description && <Typography {...styles.description}>{parse(description, xParserOptions)}</Typography>}
            <StyledWrapper {...styles.categoriesContainer}>
                {categoriesList.map(category => (
                    <FeaturedCategoryItem
                        key={category.fields?.name}
                        category={category}
                        path={categoryMap?.[category.fields.destination.value]?.path}
                        styles={styles}
                    />
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: connect(null, mapDispatchToProps)(FeaturedCategories),
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Contextual",
            },
            {
                name: fields.description,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.categoriesList,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Category name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Url",
                        },
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
